import React from 'react';
import styled from 'styled-components';

import {SubnavSectionName, SubnavSectionNameProps} from './SubnavSectionName';
import {GridContainer, VerticalSpacer} from '..';
import {ThemeOverrideWrapper, ThemeOverrideOptions} from '../theme';
import {GradientWrapper} from '../gradient';
import {useBreakpointDown, useIsTwinmotionTheme} from 'epic-ue-theme';
import classNames from 'classnames';
export interface SubnavSectionConfigProps extends SubnavSectionNameProps {
    id?: string;
    theme?: ThemeOverrideOptions;
    hideTopSectionPadding?: boolean;
    hideItemSeperator?: boolean;
    itemNumberColorOverride?: string;
}

export type SubnavSectionVariantOptions = 'gradient' | '';
export interface SubnavSectionProps {
    children: React.ReactNode;
    hideBottomSectionPadding?: boolean;
    hideBottomSectionPaddingSM?: boolean;
    hideTopSectionPadding?: boolean;
    hideSectionName?: boolean;
    sectionConfig: SubnavSectionConfigProps;
    variant?: SubnavSectionVariantOptions;
}

export const noVerticalPadding = {
    paddingTop: 0,
    paddingBottom: 0
};

const Styled = styled.div`
    background: ${(p) => p.theme.palette.background.default};
    color: ${(p) => p.theme.palette.text.primary};

    &&.twinmotion .paragraph {
        color: ${(p) => p.theme.palette.text.primary};
    }

    /**
     * Prevent any spotlights from making horizontal scroll bars
     * https://jira.it.epicgames.com/browse/MW-1838
     */
    ${(p) => p.theme.breakpoints.down('md')} {
        overflow: hidden;
    }
`;

export const SubnavSection = ({
    children,
    hideTopSectionPadding = false,
    hideBottomSectionPadding = false,
    hideBottomSectionPaddingSM = false,
    hideSectionName = false,
    sectionConfig = {} as SubnavSectionConfigProps,
    variant = undefined
}: SubnavSectionProps): JSX.Element => {
    const {
        accentColor = 'sky',
        color = 'primary',
        id,
        index,
        theme,
        sectionName,
        sectionNameAlign,
        itemNumberColorOverride
    } = sectionConfig;
    const useGradient = variant === 'gradient';
    const subnavSectionNameProps = {
        sectionName,
        sectionNameAlign,
        index,
        itemNumberColorOverride
    } as SubnavSectionNameProps;

    const isSm = useBreakpointDown('sm');
    const isTwin = useIsTwinmotionTheme();

    if (useGradient) {
        subnavSectionNameProps.color = color;
    } else {
        subnavSectionNameProps.accentColor = accentColor;
    }

    const sectionContent = (
        <>
            {!hideTopSectionPadding && <VerticalSpacer variant="section" />}
            {!hideSectionName && (
                <GridContainer>
                    <SubnavSectionName {...subnavSectionNameProps} />
                </GridContainer>
            )}
            {children}
            {!hideBottomSectionPadding && !(isSm && hideBottomSectionPaddingSM) && (
                <VerticalSpacer variant="section" />
            )}
        </>
    );

    const sectionContentWrapper = (
        <GradientWrapper variant="radial" accentColor={accentColor}>
            {sectionContent}
        </GradientWrapper>
    );

    return (
        <ThemeOverrideWrapper theme={theme}>
            <Styled className={classNames(theme, {twinmotion: isTwin})} id={id}>
                {useGradient ? sectionContentWrapper : sectionContent}
            </Styled>
        </ThemeOverrideWrapper>
    );
};
